/* ─────────────────────────────────────────────────────────────────────────────
   Merchant Setup → Shopify EDD Pickup Mapping — API calls (admin panel).

   Backend: proship_mongo /api/pickup-drop-mapping/*. Every call carries merchantOid (the merchant
   being edited; required for admin tokens, the backend checks the admin may access it). JSON responses
   use the GenericAPIResponse envelope — the payload is `responseObj`; CSV endpoints stream the file.

     GET    config?merchantOid              → { enabled, defaultPickupPincode, mappings[{dropType, dropValue, pickupPincode}], lastUploadFileName, lastUploadAt, ... }
     GET    states                          → ["Andhra Pradesh", ...]   exact spellings accepted in a mapping
     GET    pickup-pincodes?merchantOid     → ["110001", ...]           the merchant's saved pickup locations (hints only)
     GET    template                        → CSV (every state + 2 example rows)
     GET    download?merchantOid            → CSV of the current mappings (re-uploadable)
     GET    pickup?merchantOid&dropPincode  → { found, pickupPincode, matchedBy, matchedDrop, dropState, dropCity, message }
     PUT    settings?merchantOid {enabled, defaultPickupPincode}   (a key left out of the body is left unchanged)
     POST   upload?merchantOid&confirm (file) → { confirmed, rowsInFile, validRows, stateRows, pincodeRows, blankPickupRows, existingMappings, invalidRows[] }
     POST   mapping?merchantOid {drop, pickupPincode} → config
     DELETE mapping?merchantOid&drop        → config
     DELETE mappings?merchantOid            → config

   Blob downloads use saveBlob / filenameFromResponse from freight-profitability/freightPnlUtils.
   ──────────────────────────────────────────────────────────────────────────── */
import { apiConstants, deleteGeneric, get, post, put } from '../../common/constants'

const BASE = apiConstants.PICKUP_DROP_MAPPING

const qs = (params) => new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))).toString()

/** Unwraps the GenericAPIResponse envelope. */
const body = (r) => r.data?.responseObj

/** API bound to one merchant, in the shape ShopifyEddLocationPanel expects. */
export const apiFor = (merchantOid) => ({
  fetchConfig: () => get(`${BASE}/config`, { merchantOid }).then(body),
  fetchStates: () => get(`${BASE}/states`).then((r) => body(r) || []),
  fetchPickupPincodes: () => get(`${BASE}/pickup-pincodes`, { merchantOid }).then((r) => body(r) || []),
  resolvePickup: (dropPincode) => get(`${BASE}/pickup`, { merchantOid, dropPincode }).then(body),
  saveSettings: (settings) => put(`${BASE}/settings?${qs({ merchantOid })}`, settings).then(body),
  addMapping: (row) => post(`${BASE}/mapping?${qs({ merchantOid })}`, row).then(body),
  deleteMapping: (drop) => deleteGeneric(`${BASE}/mapping?${qs({ merchantOid, drop })}`).then(body),
  clearMappings: () => deleteGeneric(`${BASE}/mappings?${qs({ merchantOid })}`).then(body),
  downloadTemplate: () => get(`${BASE}/template`, {}, { responseType: 'blob' }),
  downloadMappings: () => get(`${BASE}/download`, { merchantOid }, { responseType: 'blob' }),
  uploadMappings: ({ file, confirm }) => {
    const form = new FormData()
    form.append('file', file)
    return post(`${BASE}/upload?${qs({ merchantOid, confirm: confirm ? 'true' : 'false' })}`, form, { headers: { 'Content-Type': 'multipart/form-data' } }).then(body)
  },
})

/** Server message for a failed call (validation texts are written for the user, so show them as is). */
export const errorText = (e, fallback = 'Something went wrong') => e?.response?.data?.message || e?.message || fallback
